import { createMemo } from 'solid-js';
import { taxFor } from '~data';
import { sumOrNull } from '~/lib/format';
import { payments } from '~/stores/dividend-store';
import { resolved } from '~/stores/portfolio-store';

// Cost base and total return — everything a position's payment history implies.
// Both come from one pass over the payments, which is why they live together.
//
// Adjusted cost base, with return of capital carried against it.
//
// A covered-call ETF pays most of its distribution as return of capital: it is
// not income, it is your own money handed back, so it is not taxed when received
// and instead reduces what the position cost you. Book cost alone therefore
// overstates the cost base — badly, for this pool, where RoC runs 70–100% of the
// distribution — and every gain measured against it is understated by the same
// amount. That gap compounds monthly and is exactly what the site promises is
// "carried for you, per holding and per account".
//
// The published table is dollars per unit per year, so the share of a payment
// that was RoC is that year's returnOfCapital ÷ totalPerUnit. Applying the share
// to each payment rather than multiplying units by the annual per-unit figure is
// what makes a position bought mid-year come out right: the payment amounts
// already reflect the units held at each ex-date.

interface Carried {
    roc: number;
    /** Every dollar the position has paid out, whatever its tax character. */
    received: number;
    /** True when any payment fell in a year the fund has not published yet. */
    estimated: boolean;
}

/**
 * The RoC share of a payment made in `year`.
 *
 * Funds publish months into the following year, so a payment made this year has
 * no row of its own. The most recent published year stands in — the composition
 * of these funds is stable and a stale share beats pretending the RoC was zero,
 * which is the only other option and is wrong by the entire amount. It is
 * flagged rather than hidden.
 */
const shareFor = (symbol: string, year: number): { share: number; estimated: boolean } | null => {
    const rows = taxFor(symbol);
    if (rows.length === 0) return null;

    const exact = rows.find((r) => r.year === year);
    const row = exact ?? rows[rows.length - 1];
    if (row.totalPerUnit <= 0) return null;

    return { share: row.returnOfCapital / row.totalPerUnit, estimated: exact == null };
};

const carried = createMemo(() => {
    const byPosition = new Map<string, Carried>();

    for (const p of payments()) {
        const key = `${p.accountId} ${p.symbol}`;
        const entry = byPosition.get(key) ?? { roc: 0, received: 0, estimated: false };

        // Received counts every payment. RoC only counts those whose year has a
        // published split — an unsplittable payment is still cash you got, and
        // dropping it from the total would understate the return.
        entry.received += p.amount;

        // Bucketed on the year the cash arrived, which is the year it is
        // reported in — falling back to ex-date, same as everywhere else.
        const year = Number((p.payDate ?? p.exDate).slice(0, 4));
        const s = shareFor(p.symbol, year);
        if (s != null) {
            entry.roc += p.amount * s.share;
            entry.estimated = entry.estimated || s.estimated;
        }

        byPosition.set(key, entry);
    }

    return byPosition;
});

/** Dollars of return of capital received on one position. */
export const rocReceived = (accountId: string, symbol: string): number => carried().get(`${accountId} ${symbol}`)?.roc ?? 0;

/** Whether any of that RoC used a prior year's published share. */
export const rocIsEstimated = (accountId: string, symbol: string): boolean => carried().get(`${accountId} ${symbol}`)?.estimated ?? false;

/**
 * What the position actually cost, after return of capital.
 *
 * Floored at zero: once RoC has taken the cost base to nothing, the excess was
 * already taxable as a capital gain in the year it was received rather than
 * driving the base negative. Same rule as the site's ACB calculator.
 */
export const adjustedCostBase = (accountId: string, symbol: string, bookCost: number): number => Math.max(0, bookCost - rocReceived(accountId, symbol));

/** Portfolio-wide RoC received, for the totals rail. */
export const totalRoc = createMemo(() => [...carried().values()].reduce((sum, c) => sum + c.roc, 0));

// --- total return -----------------------------------------------------------

/** Every dollar one position has paid out to date. */
export const received = (accountId: string, symbol: string): number => carried().get(`${accountId} ${symbol}`)?.received ?? 0;

/**
 * What the position has actually made: what it is worth now, plus every dollar
 * it has paid out, less what it cost to buy.
 *
 * Against BOOK COST, not the adjusted cost base — and that is the whole trap
 * here. Return of capital has already been subtracted from the ACB, which
 * inflates `value - ACB` by exactly the RoC received; adding the distributions
 * on top of that would count every RoC dollar a second time. On this pool, where
 * RoC runs 70-84% of the distribution, that would overstate the return by nearly
 * the whole payout history. Book cost is the cash that left, received is the
 * cash that came back, value is what is still on the table — each dollar once.
 *
 * Null while the position is unpriced: there is no honest ending value to use.
 *
 * Simple return on cost, not annualised and not money-weighted. Two positions
 * with the same total return bought years apart did not perform the same, and
 * this does not claim they did — an IRR needs the cash flows dated, which is a
 * different figure and deserves its own column rather than being smuggled in
 * under this name.
 */
export const totalReturn = (accountId: string, symbol: string, bookCost: number, value: number | null): number | null =>
    value == null ? null : value + received(accountId, symbol) - bookCost;

export const totalReturnPct = (accountId: string, symbol: string, bookCost: number, value: number | null): number | null => {
    const amount = totalReturn(accountId, symbol, bookCost, value);
    return amount == null || bookCost <= 0 ? null : (amount / bookCost) * 100;
};

/** The sum of every position's total return, so the rail reconciles against the
    column by eye. Null if anything is unpriced, rather than a total that looks
    complete while silently missing a position. */
export const totalReturnAmount = createMemo(() => sumOrNull(resolved().map((r) => totalReturn(r.accountId, r.symbol, r.holding.bookCost, r.value))));

export const totalReceivedAmount = createMemo(() => [...carried().values()].reduce((sum, c) => sum + c.received, 0));
